<template>
<div>
    <Breadcrumbs main="Admin" submain="Blog" :title="isEdit ? 'Blog Edit' : 'Blog Create'" />
    <!-- Container-fluid starts-->
    <div class="container-fluid">
        <div v-if="isEdit && !loaded">
            <loader />
        </div>

        <div v-else class="row">
            <!-- Editor -->
            <div class="col-xl-7">
                <!-- Post details -->
                <px-card>
                    <div slot="with-padding">
                        <div class="form theme-form">
                            <h6 class="section-title">Post Details</h6>

                            <div class="form-group">
                                <label>Title <span class="text-danger">*</span></label>
                                <input class="form-control" type="text" placeholder="e.g. One-Page Resume vs. Two-Page Resume" v-model="blog.title" @input="onTitleInput">
                            </div>

                            <div class="form-group">
                                <label>Slug</label>
                                <div class="input-group">
                                    <div class="input-group-prepend">
                                        <span class="input-group-text slug-prefix">/blog-details/</span>
                                    </div>
                                    <input class="form-control" type="text" placeholder="one-page-resume-vs-two-page-resume" v-model="blog.slug" @input="slugTouched = true">
                                </div>
                            </div>

                            <div class="form-group">
                                <label>Summary</label>
                                <textarea class="form-control" rows="3" placeholder="Short intro shown under the title and on the blog list" v-model="blog.summary"></textarea>
                            </div>

                            <div class="row">
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>Banner (hero image)</label>
                                        <div class="image-picker">
                                            <img v-if="blog.banner" :src="imgSrc(blog.banner)" alt="">
                                            <input type="file" accept="image/*" @change="onImageChange($event, blog, 'banner')">
                                        </div>
                                    </div>
                                </div>
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>Thumbnail (blog list card)</label>
                                        <div class="image-picker">
                                            <img v-if="blog.thumbnail" :src="imgSrc(blog.thumbnail)" alt="">
                                            <input type="file" accept="image/*" @change="onImageChange($event, blog, 'thumbnail')">
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>Meta Title (SEO)</label>
                                        <input class="form-control" type="text" :placeholder="blog.title ? blog.title + ' | QuickCV' : 'Defaults to title'" v-model="blog.meta_title">
                                    </div>
                                </div>
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>Meta Description (SEO)</label>
                                        <input class="form-control" type="text" placeholder="Defaults to summary" v-model="blog.meta_description">
                                    </div>
                                </div>
                            </div>

                            <div class="d-flex" style="gap: 32px;">
                                <b-form-checkbox v-model="blog.is_published" switch>Published</b-form-checkbox>
                                <b-form-checkbox v-model="blog.is_featured" switch>Featured</b-form-checkbox>
                            </div>
                        </div>
                    </div>
                </px-card>

                <!-- Content sections -->
                <px-card>
                    <div slot="with-padding">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="section-title mb-0">Content Sections</h6>
                            <small class="text-muted">{{ blog.content.length }} sections · ~{{ readTime }} min read</small>
                        </div>

                        <div v-if="!blog.content.length" class="empty-blocks">
                            No sections yet. Add one below to start writing.
                        </div>

                        <div v-for="(block, index) in blog.content" :key="block._key" class="block-card" :style="{ borderLeftColor: block.background || '#e5e7eb' }">
                            <div class="block-header" :style="{ background: block.background || '#f9fafb' }">
                                <div class="d-flex align-items-center" style="gap: 10px;">
                                    <span class="block-number">{{ index + 1 }}</span>
                                    <feather :type="blockMeta(block.type).icon" size="16px"></feather>
                                    <b>{{ blockMeta(block.type).label }}</b>
                                </div>
                                <div class="block-tools">
                                    <feather type="arrow-up" size="18px" :class="{ disabled: index === 0 }" v-b-tooltip.hover title="Move up" @click="moveBlock(index, -1)"></feather>
                                    <feather type="arrow-down" size="18px" :class="{ disabled: index === blog.content.length - 1 }" v-b-tooltip.hover title="Move down" @click="moveBlock(index, 1)"></feather>
                                    <feather type="copy" size="18px" v-b-tooltip.hover title="Duplicate" @click="duplicateBlock(index)"></feather>
                                    <feather type="trash-2" size="18px" class="danger" v-b-tooltip.hover title="Remove" @click="removeBlock(index)"></feather>
                                </div>
                            </div>

                            <div class="block-body">
                                <div class="bg-picker">
                                    <span class="text-muted">Background</span>
                                    <span class="swatch none" :class="{ active: !block.background }" v-b-tooltip.hover title="None" @click="block.background = null"></span>
                                    <span v-for="color in backgroundPresets" :key="color" class="swatch" :class="{ active: block.background === color }" :style="{ background: color }" v-b-tooltip.hover :title="color" @click="block.background = color"></span>
                                    <label class="swatch custom" v-b-tooltip.hover title="Custom color" :class="{ active: block.background && !backgroundPresets.includes(block.background) }" :style="block.background && !backgroundPresets.includes(block.background) ? { background: block.background } : {}">
                                        <feather v-if="!block.background || backgroundPresets.includes(block.background)" type="plus" size="12px"></feather>
                                        <input type="color" :value="block.background || '#ffffff'" @input="block.background = $event.target.value.toUpperCase()">
                                    </label>
                                </div>

                                <!-- Heading -->
                                <template v-if="block.type === 'heading'">
                                    <div class="d-flex" style="gap: 8px;">
                                        <select class="form-control" style="width: 90px;" v-model="block.level">
                                            <option value="h2">H2</option>
                                            <option value="h3">H3</option>
                                        </select>
                                        <input class="form-control" type="text" placeholder="Section heading" v-model="block.text">
                                    </div>
                                </template>

                                <!-- Paragraph -->
                                <template v-else-if="block.type === 'paragraph'">
                                    <ckeditor :editor="editor" v-model="block.html"></ckeditor>

                                    <div v-if="block.button" class="paragraph-button">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <label class="mb-0"><b>Button</b></label>
                                            <a href="#" class="text-danger" @click.prevent="block.button = null">Remove button</a>
                                        </div>
                                        <div class="d-flex mb-2" style="gap: 8px;">
                                            <input class="form-control" type="text" placeholder="Button text, e.g. Learn More" v-model="block.button.text">
                                            <input class="form-control" type="url" placeholder="https://..." v-model="block.button.link">
                                        </div>
                                        <div class="d-flex flex-wrap align-items-center" style="gap: 16px;">
                                            <b-form-radio-group v-model="block.button.align" :options="[{ text: 'Left', value: 'left' }, { text: 'Center', value: 'center' }, { text: 'Right', value: 'right' }]" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
                                            <b-form-radio-group v-model="block.button.style" :options="[{ text: 'Filled', value: 'filled' }, { text: 'Outline', value: 'outline' }]" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
                                            <b-form-checkbox v-model="block.button.new_tab" switch>Open in new tab</b-form-checkbox>
                                        </div>
                                    </div>
                                    <a v-else href="#" class="add-paragraph-button" @click.prevent="addParagraphButton(block)">+ Add button</a>
                                </template>

                                <!-- Image -->
                                <template v-else-if="block.type === 'image'">
                                    <div class="image-picker mb-2">
                                        <img v-if="block.image" :src="imgSrc(block.image)" alt="">
                                        <input type="file" accept="image/*" @change="onImageChange($event, block, 'image')">
                                    </div>
                                    <div class="d-flex" style="gap: 8px;">
                                        <input class="form-control" type="text" placeholder="Alt text (for SEO & accessibility)" v-model="block.alt">
                                        <input class="form-control" type="text" placeholder="Caption (optional)" v-model="block.caption">
                                    </div>
                                </template>

                                <!-- Image + Text -->
                                <template v-else-if="block.type === 'image_text'">
                                    <div class="d-flex align-items-center mb-2" style="gap: 12px;">
                                        <label class="mb-0">Image position</label>
                                        <b-form-radio-group v-model="block.position" :options="[{ text: 'Left', value: 'left' }, { text: 'Right', value: 'right' }]" buttons button-variant="outline-primary" size="sm"></b-form-radio-group>
                                    </div>
                                    <div class="image-picker mb-2">
                                        <img v-if="block.image" :src="imgSrc(block.image)" alt="">
                                        <input type="file" accept="image/*" @change="onImageChange($event, block, 'image')">
                                    </div>
                                    <input class="form-control mb-2" type="text" placeholder="Alt text" v-model="block.alt">
                                    <input class="form-control mb-2" type="text" placeholder="Title (optional)" v-model="block.title">
                                    <ckeditor :editor="editor" v-model="block.html"></ckeditor>
                                </template>

                                <!-- Quote -->
                                <template v-else-if="block.type === 'quote'">
                                    <textarea class="form-control mb-2" rows="2" placeholder="Quote or key takeaway" v-model="block.text"></textarea>
                                    <input class="form-control" type="text" placeholder="Attribution (optional)" v-model="block.author">
                                </template>
                            </div>
                        </div>

                        <div class="add-block">
                            <span class="text-muted mr-2">Add section:</span>
                            <button v-for="meta in blockTypes" :key="meta.type" type="button" class="btn btn-outline-primary btn-sm add-btn" @click="addBlock(meta.type)">
                                <feather :type="meta.icon" size="14px"></feather>
                                {{ meta.label }}
                            </button>
                        </div>
                    </div>
                </px-card>

                <div class="mb-4">
                    <button @click="onSubmit" class="btn btn-success mr-3" :disabled="isDisabled">{{ isEdit ? 'Update Blog' : 'Create Blog' }}</button>
                    <router-link to="/blog/list">
                        <button class="btn btn-danger">Go Back</button>
                    </router-link>
                </div>
            </div>

            <!-- Live preview -->
            <div class="col-xl-5">
                <div class="preview-sticky">
                    <div class="preview-frame">
                        <div class="preview-bar">
                            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
                            <span class="preview-url">createquickcv.com/blog-details/{{ blog.slug || 'your-slug' }}</span>
                        </div>

                        <article class="qcv-blog">
                            <h1 class="qcv-title">{{ blog.title || 'Your blog title' }}</h1>
                            <p v-if="blog.summary" class="qcv-summary">{{ blog.summary }}</p>
                            <img v-if="blog.banner" class="qcv-hero" :src="imgSrc(blog.banner)" :alt="blog.title">

                            <section v-for="(block, index) in blog.content" :key="block._key" class="qcv-section" :class="bandClass(index)" :style="{ background: block.background || null }">
                                <component v-if="block.type === 'heading'" :is="block.level" :class="block.level === 'h2' ? 'qcv-h2' : 'qcv-h3'">{{ block.text }}</component>

                                <div v-else-if="block.type === 'paragraph'" class="qcv-text">
                                    <div v-html="block.html"></div>
                                    <div v-if="block.button && block.button.text" class="qcv-button-row" :style="{ textAlign: block.button.align }">
                                        <a :href="block.button.link || '#'" :class="['qcv-button', block.button.style]" :target="block.button.new_tab ? '_blank' : null" rel="noopener noreferrer" @click.prevent>{{ block.button.text }}</a>
                                    </div>
                                </div>

                                <figure v-else-if="block.type === 'image' && block.image" class="qcv-figure">
                                    <img :src="imgSrc(block.image)" :alt="block.alt">
                                    <figcaption v-if="block.caption">{{ block.caption }}</figcaption>
                                </figure>

                                <div v-else-if="block.type === 'image_text'" class="qcv-split" :class="{ reverse: block.position === 'right' }">
                                    <img v-if="block.image" :src="imgSrc(block.image)" :alt="block.alt">
                                    <div>
                                        <h3 v-if="block.title" class="qcv-h3" style="margin-top: 0;">{{ block.title }}</h3>
                                        <div class="qcv-text" v-html="block.html"></div>
                                    </div>
                                </div>

                                <blockquote v-else-if="block.type === 'quote'" class="qcv-quote">
                                    <p>{{ block.text }}</p>
                                    <cite v-if="block.author">— {{ block.author }}</cite>
                                </blockquote>
                            </section>

                            <p v-if="!blog.content.length" class="qcv-empty">Add content sections to see them here.</p>
                        </article>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Container-fluid Ends-->
</div>
</template>

<script>
import {
    mapState
} from 'vuex';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
    myHost
} from '../../../_helpers/host';
import loader from '../loader/loader.vue';

const BLOCK_TYPES = [
    { type: 'heading', label: 'Heading', icon: 'type', defaults: { level: 'h2', text: '' } },
    { type: 'paragraph', label: 'Paragraph', icon: 'align-left', defaults: { html: '', button: null } },
    { type: 'image', label: 'Image', icon: 'image', defaults: { image: null, alt: '', caption: '' } },
    { type: 'image_text', label: 'Image + Text', icon: 'columns', defaults: { image: null, alt: '', position: 'left', title: '', html: '' } },
    { type: 'quote', label: 'Quote', icon: 'message-square', defaults: { text: '', author: '' } }
];

// Section tints used on createquickcv.com blog pages
const BACKGROUND_PRESETS = ['#EEF2FF', '#FFFAEA', '#EFFEF6', '#F9F0FF'];

let keySeed = 0;
const newKey = () => `b${Date.now()}_${keySeed++}`;

const emptyBlog = () => ({
    title: '',
    slug: '',
    summary: '',
    banner: null,
    thumbnail: null,
    meta_title: '',
    meta_description: '',
    is_published: false,
    is_featured: false,
    content: []
});

export default {
    components: {
        loader
    },

    data() {
        return {
            host: myHost.host,
            editor: ClassicEditor,
            blockTypes: BLOCK_TYPES,
            backgroundPresets: BACKGROUND_PRESETS,
            blog: emptyBlog(),
            slugTouched: false,
            loaded: false,
            isDisabled: false
        };
    },

    computed: {
        ...mapState({
            blog_detail: state => state.blog.blog_detail
        }),

        isEdit() {
            return !!this.$route.params.slug;
        },

        readTime() {
            const text = this.blog.content
                .map(b => [b.text, b.title, b.html].filter(Boolean).join(' '))
                .join(' ')
                .replace(/<[^>]*>/g, ' ');
            const words = text.split(/\s+/).filter(Boolean).length;
            return Math.max(1, Math.round(words / 200));
        }
    },

    mounted() {
        // The theme's .page-wrapper uses overflow: hidden, which makes it the sticky
        // container and stops the preview from sticking. `clip` clips the same way
        // without creating a scroll container.
        this.pageWrapper = this.$el.closest('.page-wrapper');
        if (this.pageWrapper) {
            this.pageWrapperOverflow = this.pageWrapper.style.overflow;
            this.pageWrapper.style.overflow = 'clip';
        }

        if (this.isEdit) {
            this.$store.dispatch("blog/fetchBlogDetail", this.$route.params.slug).then(() => {
                const detail = JSON.parse(JSON.stringify(this.blog_detail || {}));
                this.blog = {
                    ...emptyBlog(),
                    ...detail,
                    content: (detail.content || []).map(b => ({ background: null, ...b, _key: newKey() }))
                };
                this.slugTouched = true;
                this.loaded = true;
            });
        }
    },

    beforeDestroy() {
        if (this.pageWrapper) {
            this.pageWrapper.style.overflow = this.pageWrapperOverflow;
        }
    },

    methods: {
        blockMeta(type) {
            return BLOCK_TYPES.find(b => b.type === type) || { label: type, icon: 'square' };
        },

        // New uploads are base64 data URLs; saved images come back as paths relative to host
        imgSrc(value) {
            if (!value) return '';
            return /^(data:|https?:)/.test(value) ? value : this.host + value;
        },

        slugify(text) {
            return text.toLowerCase().trim()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
        },

        onTitleInput() {
            if (!this.slugTouched) {
                this.blog.slug = this.slugify(this.blog.title);
            }
        },

        onImageChange(e, target, field) {
            const image = e.target.files[0];
            if (!image) return;
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onload = ev => {
                target[field] = ev.target.result;
            };
        },

        addBlock(type) {
            const meta = this.blockMeta(type);
            this.blog.content.push({ _key: newKey(), type, background: null, ...JSON.parse(JSON.stringify(meta.defaults)) });
        },

        // Consecutive sections with the same background render as one band
        bandClass(index) {
            const content = this.blog.content;
            const bg = content[index].background;
            if (!bg) return {};
            return {
                tinted: true,
                'band-start': index === 0 || content[index - 1].background !== bg,
                'band-end': index === content.length - 1 || content[index + 1].background !== bg
            };
        },

        addParagraphButton(block) {
            // $set: paragraphs loaded from the API may not have a `button` key yet
            this.$set(block, 'button', { text: '', link: '', align: 'left', style: 'filled', new_tab: true });
        },

        duplicateBlock(index) {
            const copy = { ...JSON.parse(JSON.stringify(this.blog.content[index])), _key: newKey() };
            this.blog.content.splice(index + 1, 0, copy);
        },

        moveBlock(index, direction) {
            const target = index + direction;
            if (target < 0 || target >= this.blog.content.length) return;
            const [block] = this.blog.content.splice(index, 1);
            this.blog.content.splice(target, 0, block);
        },

        removeBlock(index) {
            this.blog.content.splice(index, 1);
        },

        buildPayload() {
            return {
                ...this.blog,
                slug: this.blog.slug || this.slugify(this.blog.title),
                read_time: this.readTime,
                content: this.blog.content.map(({ _key, ...rest }) => rest)
            };
        },

        onSubmit() {
            if (!this.blog.title.trim()) {
                Swal.fire({ title: "Missing title", text: "Please enter a blog title.", icon: "warning" });
                return;
            }
            if (!this.blog.content.length) {
                Swal.fire({ title: "No content", text: "Add at least one content section.", icon: "warning" });
                return;
            }

            this.isDisabled = true;
            const action = this.isEdit
                ? this.$store.dispatch("blog/updateBlog", { slug: this.$route.params.slug, blog: this.buildPayload() })
                : this.$store.dispatch("blog/addBlog", { blog: this.buildPayload() });

            action.then(response => {
                if (response && response.code == 200) {
                    Swal.fire({
                        title: "Success!",
                        text: response.response,
                        icon: "success"
                    });
                    setTimeout(() => {
                        this.$router.push("/blog/list");
                    }, 2000);
                } else {
                    this.isDisabled = false;
                    Swal.fire({
                        title: "Error!",
                        text: response ? response.response : 'Something went wrong',
                        icon: "error"
                    });
                }
            })
        }
    }
};
</script>

<style scoped>
.section-title {
    font-weight: 700;
    margin-bottom: 16px;
}

.slug-prefix {
    font-size: 13px;
    color: #6b7280;
}

.image-picker {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.image-picker img {
    height: 80px;
    max-width: 160px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
}

/* Block editor */
.empty-blocks {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 28px;
    text-align: center;
    color: #6b7280;
    margin-bottom: 16px;
}

.block-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 14px;
    background: #fff;
}

.block-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 14px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 8px 8px 0 0;
}

.block-number {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #7366ff;
    color: #fff;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.block-tools {
    display: flex;
    gap: 10px;
    color: #6b7280;
}

.block-tools > * {
    cursor: pointer;
}

.block-tools .disabled {
    opacity: 0.3;
    pointer-events: none;
}

.block-tools .danger {
    color: #ff595d;
}

.block-body {
    padding: 14px;
}

.bg-picker {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 13px;
}

.swatch {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1px solid #d1d5db;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 0;
    color: #6b7280;
}

.swatch.active {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px #7366ff;
}

.swatch.none {
    background: linear-gradient(135deg, #fff 45%, #ff595d 45%, #ff595d 55%, #fff 55%);
}

.swatch.custom input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
}

.add-block {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding-top: 6px;
}

.add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

/* Preview */
.preview-sticky {
    position: sticky;
    top: 90px;
}

.preview-frame {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.preview-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #f3f4f6;
    border-bottom: 1px solid #e5e7eb;
}

.preview-bar .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #d1d5db;
}

.preview-url {
    margin-left: 8px;
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.qcv-blog {
    max-height: calc(100vh - 140px);
    overflow-y: auto;
    padding: 24px 22px 32px;
    color: #1f2937;
    line-height: 1.7;
}

.qcv-title {
    font-size: 26px;
    font-weight: 800;
    line-height: 1.25;
    color: #111827;
    margin-bottom: 10px;
}

.qcv-summary {
    font-size: 15px;
    color: #4b5563;
    margin-bottom: 18px;
}

.qcv-hero {
    width: 100%;
    border-radius: 10px;
    margin-bottom: 24px;
}

.qcv-h2 {
    font-size: 21px;
    font-weight: 700;
    color: #111827;
    margin: 26px 0 10px;
}

.qcv-h3 {
    font-size: 17px;
    font-weight: 700;
    color: #111827;
    margin: 20px 0 8px;
}

.qcv-text {
    font-size: 15px;
    margin-bottom: 14px;
}

.qcv-text >>> p {
    margin-bottom: 10px;
}

.qcv-text >>> ul,
.qcv-text >>> ol {
    padding-left: 20px;
    margin-bottom: 10px;
}

.qcv-text >>> ul { list-style: disc; }
.qcv-text >>> ol { list-style: decimal; }

.qcv-figure {
    margin: 20px 0;
}

.qcv-figure img {
    width: 100%;
    border-radius: 10px;
}

.qcv-figure figcaption {
    font-size: 13px;
    color: #6b7280;
    font-style: italic;
    text-align: center;
    margin-top: 6px;
}

.qcv-split {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    margin: 20px 0;
}

.qcv-split.reverse {
    flex-direction: row-reverse;
}

.qcv-split img {
    width: 45%;
    border-radius: 10px;
    flex-shrink: 0;
}

.qcv-quote {
    border-left: 4px solid #7366ff;
    background: #f5f3ff;
    padding: 14px 18px;
    border-radius: 0 8px 8px 0;
    margin: 20px 0;
}

.qcv-quote p {
    font-size: 16px;
    font-style: italic;
    margin: 0;
}

.qcv-quote cite {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    color: #6b7280;
}

.paragraph-button {
    margin-top: 12px;
    padding: 12px;
    border: 1px dashed #d1d5db;
    border-radius: 6px;
    background: #f9fafb;
}

.add-paragraph-button {
    display: inline-block;
    margin-top: 10px;
    font-size: 13px;
    font-weight: 600;
}

/* Tinted sections bleed to the article edges; adjacent same-color sections merge into one band */
.qcv-section.tinted {
    margin: 0 -22px;
    padding: 4px 22px;
}

.qcv-section.band-start {
    padding-top: 18px;
}

.qcv-section.band-end {
    padding-bottom: 18px;
}

.qcv-button-row {
    margin: 18px 0;
}

.qcv-button {
    display: inline-block;
    font-weight: 600;
    padding: 9px 22px;
    border-radius: 999px;
    border: 2px solid #7366ff;
}

.qcv-button.filled {
    background: #7366ff;
    color: #fff;
}

.qcv-button.outline {
    background: transparent;
    color: #7366ff;
}

.qcv-empty {
    color: #9ca3af;
    text-align: center;
    padding: 32px 0;
}

@media screen and (max-width: 1199px) {
    .preview-sticky {
        position: static;
    }

    .qcv-blog {
        max-height: none;
    }
}

@media screen and (max-width: 575px) {
    .qcv-split,
    .qcv-split.reverse {
        flex-direction: column;
    }

    .qcv-split img {
        width: 100%;
    }
}
</style>
